import { cva } from "class-variance-authority";
import * as React from "react";

import type { ButtonStandardProps } from "./types";

import { cn } from "../../../lib/utils";
import { getButtonIconSize, getIconColor } from "./utils";

import { Icon } from "../Icon";

const buttonStandardVariants = cva(
	"relative inline-flex w-full items-center justify-center rounded-1 font-semibold hover:cursor-pointer",
	{
		variants: {
			variant: {
				solid: "border-none",
				outline: "border border-solid border-neutral-3 bg-transparent text-neutral-7",
				text: "border-transparent bg-transparent hover:underline active:underline",
				micro: "border-transparent",
			},
			color: {
				primary: "",
				green: "",
				red: "",
				neutral: "",
			},
			size: {
				large: "h-10 gap-1 px-4 text-paragraph-2",
				medium: "h-8 gap-1 px-2 text-paragraph-3",
				small: "h-6 gap-1 px-1 text-caption",
			},
			disabled: {
				true: "pointer-events-none opacity-40",
			},
		},
		compoundVariants: [
			// Solid variants
			{
				variant: "solid",
				color: "primary",
				className: "bg-primary-5 text-neutral-0 hover:bg-primary-7 active:bg-primary-8",
			},
			{
				variant: "solid",
				color: "green",
				className: "bg-green-5 text-neutral-0 hover:bg-green-7 active:bg-green-8",
			},
			{
				variant: "solid",
				color: "red",
				className: "bg-red-5 text-neutral-0 hover:bg-red-7 active:bg-red-8",
			},
			{
				variant: "solid",
				color: "neutral",
				className: "bg-neutral-5 text-neutral-0 hover:bg-neutral-7 active:bg-neutral-8",
			},
			// Text variants
			{
				variant: "text",
				className: "min-w-0 p-0",
			},
			{
				variant: "text",
				color: "primary",
				className: "text-primary-5 hover:text-primary-7 active:text-primary-8",
			},
			{
				variant: "text",
				color: "green",
				className: "text-green-5 hover:text-green-7 active:text-green-8",
			},
			{
				variant: "text",
				color: "red",
				className: "text-red-5 hover:text-red-7 active:text-red-8",
			},
			{
				variant: "text",
				color: "neutral",
				className: "text-neutral-5 hover:text-neutral-7 active:text-neutral-8",
			},
			// Outline hover states
			{
				variant: "outline",
				color: "neutral",
				className: "hover:bg-neutral-1 active:border-neutral-3 active:bg-neutral-2",
			},
			{
				variant: "outline",
				color: "green",
				className:
					"border-green-5 text-green-5 hover:bg-green-1 active:border-green-6 active:bg-green-2 active:text-green-6",
			},
			{
				variant: "outline",
				color: "red",
				className:
					"border-red-5 text-red-5 hover:bg-red-1 active:border-red-6 active:bg-red-2 active:text-red-6",
			},
			{
				variant: "outline",
				color: "primary",
				className:
					"border-primary-5 text-primary-5 hover:bg-primary-1 active:border-primary-6 active:bg-primary-2 active:text-primary-6",
			},
			// Adjust padding for outline variant to fix height
			{
				variant: "outline",
				size: "large",
				className: "py-[7px]",
			},
			{
				variant: "outline",
				size: "medium",
				className: "py-[7px]",
			},
			{
				variant: "outline",
				size: "small",
				className: "py-[3px]",
			},
			// Micro base styling (overrides size)
			{
				variant: "micro",
				className: "h-5 gap-1 px-1 text-[10px] leading-[12px] text-neutral-9",
			},
			// Micro color variants
			{
				variant: "micro",
				color: "primary",
				className: "bg-primary-1 hover:bg-primary-5 hover:text-neutral-0 active:bg-primary-8",
			},
			{
				variant: "micro",
				color: "green",
				className: "bg-green-1 hover:bg-green-5 hover:text-neutral-0 active:bg-green-8",
			},
			{
				variant: "micro",
				color: "red",
				className: "bg-red-1 hover:bg-red-5 hover:text-neutral-0 active:bg-red-8",
			},
			{
				variant: "micro",
				color: "neutral",
				className: "bg-neutral-1 hover:bg-neutral-5 hover:text-neutral-0 active:bg-neutral-8",
			},
		],
		defaultVariants: {
			variant: "solid",
			color: "primary",
			size: "medium",
			disabled: false,
		},
	}
);

/**
 * A basic, standard button component.
 *
 * This component is intended for general button interactions within the application
 * where immediate actions are triggered without asynchronous operations or page navigation.
 * For scenarios involving loading states during asynchronous requests or for navigation purposes,
 * please use the dedicated loading button and link button components respectively.
 */
const ButtonStandard = React.forwardRef<HTMLButtonElement, ButtonStandardProps>(
	(
		{
			variant = "solid",
			color = "primary",
			size = "medium",
			dangerouslySetClassName,
			iconLeft,
			iconRight,
			children,
			disabled = false,
			type = "button",
			...props
		},
		ref
	) => {
		const iconSize = getButtonIconSize(size);
		const iconColor = getIconColor(variant, color);

		return (
			<button
				className={cn(
					buttonStandardVariants({ variant, color, size, disabled, className: dangerouslySetClassName })
				)}
				ref={ref}
				disabled={disabled}
				type={type}
				{...props}
			>
				{iconLeft && <Icon icon={iconLeft} size={iconSize} color={iconColor} />}
				<span className="whitespace-nowrap text-center">{children}</span>
				{iconRight && <Icon icon={iconRight} size={iconSize} color={iconColor} />}
			</button>
		);
	}
);

ButtonStandard.displayName = "ButtonStandard";

export { ButtonStandard, buttonStandardVariants, ButtonStandardProps };
